import { useRef, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useConfig } from "../context/useConfig";
import { fadeInUp, staggerChildren, fadeIn } from "../utils/motionVariants";
import { SafeImage } from "./SafeImage";

function ProjectCard({ project, index, placeholderImage, reduceMotion, cardRef }) {
  return (
    <motion.li
      ref={cardRef}
      variants={fadeInUp(0.05 * index, reduceMotion ? 0 : 40)}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      className="group relative flex flex-col h-full overflow-hidden glass-panel glass-panel-hover transition-all"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <SafeImage
          src={project.image}
          fallbackSrc={placeholderImage}
          alt={project.name}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-black/60 via-black/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-3 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <p className="text-[10px] font-medium tracking-wide text-white/90 truncate">
            {project.tags.join(" · ")}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-6">
        <h3 className="text-base font-bold tracking-tight text-gray-900 mb-2 leading-snug">
          {project.name}
        </h3>

        <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
          {project.summary}
        </p>

        {/* Highlights — only for cards that have them; capped at 2 to stay consistent */}
        {project.highlights?.length > 0 && (
          <ul className="space-y-2 mb-4">
            {project.highlights.slice(0, 2).map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-gray-500 leading-snug">
                <span className="mt-[4px] flex-shrink-0 w-3 h-3 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="w-1 h-1 rounded-full bg-emerald-500" />
                </span>
                <span className="line-clamp-2">{h}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
          {project.tags.slice(0, 3).map((tag, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-[11px] font-medium tracking-wide"
            >
              {tag}
            </span>
          ))}
          {project.tags.length > 3 && (
            <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-500 px-2.5 py-0.5 text-[11px] font-medium">
              +{project.tags.length - 3}
            </span>
          )}
        </div>
      </div>
    </motion.li>
  );
}

function Projects() {
  const { config } = useConfig();
  const projectRefs = useRef([]);
  const reduceMotion = useReducedMotion();

  const heading = config.projectsSection?.heading || "Projects";
  const intro = config.projectsSection?.intro || "Explore some of my recent work and personal projects. Each project represents a unique challenge and solution.";
  const placeholderImage = config.projectsSection?.placeholderImage || "/projects/placeholder.svg";

  const projects = config.projects || [];

  useEffect(() => {
    projectRefs.current = projectRefs.current.slice(0, projects.length);
  }, [projects.length]);

  return (
    <section
      id="projects"
      aria-labelledby="projects-heading"
      className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-emerald-50 via-white to-cyan-50 overflow-hidden"
    >
      <div aria-hidden className="hidden sm:block absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-emerald-300/40 blur-3xl" />
      <div aria-hidden className="hidden sm:block absolute bottom-[-18%] -left-24 w-[30rem] h-[30rem] rounded-full bg-cyan-300/40 blur-3xl" />
      <div aria-hidden className="absolute inset-0 pattern-dots" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="space-y-16"
        >
          <header className="max-w-3xl mx-auto text-center">
            <motion.h2
              id="projects-heading"
              variants={fadeInUp(0, reduceMotion ? 0 : 24)}
              className="text-4xl sm:text-5xl font-extrabold tracking-tight gradient-text"
            >
              {heading}
            </motion.h2>
            <motion.p variants={fadeIn(0.15)} className="mt-6 text-lg text-gray-600 font-medium leading-relaxed">
              {intro}
            </motion.p>
          </header>

          <motion.ul
            variants={staggerChildren(0.18, 0.05)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-120px" }}
            className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10"
            role="list"
          >
            {projects.map((project, index) => (
              <ProjectCard
                key={project.name + index}
                project={project}
                index={index}
                placeholderImage={placeholderImage}
                reduceMotion={reduceMotion}
                cardRef={(el) => (projectRefs.current[index] = el)}
              />
            ))}
          </motion.ul>
        </motion.div>
      </div>
    </section>
  );
}

export default Projects;